// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper,
  Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import api from '../../utils/api';
import { dateTime } from '../../utils/dates';
import { filterItems } from '../../utils/recherche';
import ChampRecherche from '../ChampRecherche';
import { ROOM_TYPE_LABEL, ROOM_TYPES } from './vocabulaire';

// Santé de la détection de chute d'un logement. L'exploitant la consulte ici et
// n'est notifié nulle part : l'alerte va à la famille, seule à pouvoir agir sur
// place.
//
// `detection_active` vaut true, false ou null. Une box qui n'a jamais rapporté
// (add-on trop ancien) s'affiche « Inconnue », pas en vert.
function DetectionChip({ health }) {
  if (!health) {
    return <Chip label="Inconnue" size="small" variant="outlined" />;
  }
  const { detection_active: active, detector_state: state, detector_reason: reason } = health;
  if (active === false) {
    return (
      <Tooltip title={reason || `État du détecteur : ${state}`}>
        <Chip label="Interrompue" color="error" size="small" />
      </Tooltip>
    );
  }
  if (active === true) {
    const bits = [];
    if (health.last_inference_at) bits.push(`dernière analyse : ${dateTime(health.last_inference_at, 'Jamais')}`);
    if (health.cameras_configured) bits.push(`caméras : ${health.cameras_reachable}/${health.cameras_configured}`);
    return (
      <Tooltip title={bits.join(' · ') || 'Détecteur en service'}>
        <Chip label="Active" color="success" size="small" />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={
      health.reported_at
        ? `Dernier rapport : ${dateTime(health.reported_at, 'Jamais')} (état « ${state} »)`
        : "Ce boîtier n'a jamais rapporté l'état de sa détection (add-on à mettre à jour)."
    }>
      <Chip label="Inconnue" size="small" variant="outlined" />
    </Tooltip>
  );
}

const BLANK_ROOM = { name: '', room_number: '', resident_name: '', floor: '', room_type: '', description: '' };

/**
 * Onglet « Logements » de la fiche d'un foyer : la liste, le détail technique
 * de chaque logement dans son panneau dépliant (intégration Home Assistant,
 * identifiant de webhook, jetons masqués), la création et la suppression d'un
 * logement.
 *
 * La recherche et le logement déplié sont tenus par la page, pour survivre au
 * rechargement qui suit chaque geste.
 */
export default function HomesTab({
  clientId, rooms, detectorHealth, recherche, onRecherche, logementOuvert: openHome, onLogementOuvert: onHomeOpened,
  onSnack, onErreur: onError, onRecharger: onReload,
}) {
  const [roomDialog, setRoomDialog] = useState(false);
  const [roomForm, setRoomForm] = useState(BLANK_ROOM);
  const [savingRoom, setSavingRoom] = useState(false);
  const [deleteRoomTarget, setDeleteRoomTarget] = useState(null);
  const [deletingRoom, setDeletingRoom] = useState(false);

  const handleCreateRoom = async () => {
    setSavingRoom(true);
    try {
      // Pas d'`owner_id` : la route impose le compte connecté comme
      // propriétaire, pour qu'un compte d'équipe ne puisse pas attribuer une
      // pièce à un autre utilisateur. Le rattachement au client se fait à
      // l'appel suivant.
      const payload = { ...roomForm };
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const res = await api.post('/properties/', payload);
      await api.post(`/clients/${clientId}/rooms/${res.data.id}`);
      setRoomDialog(false);
      setRoomForm(BLANK_ROOM);
      onReload();
    } catch (e) {
      onError(e.response?.data?.detail || 'Erreur création chambre');
    } finally {
      setSavingRoom(false);
    }
  };

  const handleDeleteRoom = async () => {
    if (!deleteRoomTarget) return;
    setDeletingRoom(true);
    try {
      await api.delete(`/properties/${deleteRoomTarget.id}`);
      setDeleteRoomTarget(null);
      onSnack('Logement supprimé');
      onReload();
    } catch (e) {
      onError(e.response?.data?.detail || 'Erreur suppression logement');
    } finally {
      setDeletingRoom(false);
    }
  };

  const filteredHomes = useMemo(() => filterItems(rooms, recherche, (r) => [
    r.name, r.room_number, r.resident_name, r.floor, r.address, r.city, r.postal_code,
    ROOM_TYPE_LABEL[r.room_type],
  ]), [rooms, recherche]);

  return (
    <>
      <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <ChampRecherche
            valeur={recherche}
            onChange={onRecherche}
            placeholder="Rechercher un logement par nom, numéro, résident ou localisation"
            compte={filteredHomes.length}
            total={rooms.length}
            nomObjet="logement"
          />
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setRoomDialog(true)}>
            Ajouter un logement
          </Button>
        </Box>
        {rooms.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            Aucun logement rattaché à ce client
          </Paper>
        ) : (
          <Table size="small" component={Paper}>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>N° chambre</TableCell>
                <TableCell>Résident</TableCell>
                <TableCell>Étage</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>HA</TableCell>
                {/* « HA » dit que la box est configurée, « Détection » que la
                    surveillance fonctionne : une box peut être connectée avec
                    un détecteur à l'arrêt. */}
                <TableCell>Détection</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHomes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucun logement ne correspond à cette recherche.
                  </TableCell>
                </TableRow>
              ) : filteredHomes.map(r => (
                <React.Fragment key={r.id}>
                  <TableRow hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Pas de `color="action"` : blanc pur en thème sombre,
                            le pictogramme ressortirait plus que le nom du
                            logement. */}
                        <HomeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight="medium">{r.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="body2">{r.room_number || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.resident_name || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.floor || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ROOM_TYPE_LABEL[r.room_type] || r.room_type || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {r.ha_instance_url
                        ? <Chip label="Connecté" color="success" size="small" />
                        : <Chip label="Non configuré" size="small" />}
                    </TableCell>
                    <TableCell>
                      <DetectionChip health={detectorHealth[r.id]} />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Supprimer ce logement">
                          <IconButton size="small" onClick={() => setDeleteRoomTarget(r)}>
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={openHome === r.id ? 'Masquer le détail technique' : 'Détail technique (webhook, jetons)'}>
                          <IconButton size="small"
                            onClick={() => onHomeOpened(openHome === r.id ? null : r.id)}>
                            {openHome === r.id
                              ? <ExpandLessIcon fontSize="small" />
                              : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: openHome === r.id ? undefined : 'none' }}>
                      <Collapse in={openHome === r.id} timeout="auto" unmountOnExit>
                        {/* Panneau creusé dans la ligne, plus sombre que la
                            page, comme l'aperçu de la console de contrats :
                            un fond clair ferait de ce contenu technique la
                            zone la plus lumineuse de l'écran. */}
                        <Box sx={{ py: 2, px: 1, my: 1, borderRadius: 1,
                          bgcolor: 'rgba(4,9,20,.45)', border: 1, borderColor: 'divider' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Intégration Home Assistant</Typography>
                          <Divider sx={{ mb: 1.5 }} />

                          {r.ha_instance_url && (
                            /* Encart de succès de la charte : fond de la
                               couleur d'état à 10 %, bordure à 50 %, texte à
                               la couleur pleine. `success.dark` est la
                               variante prévue pour du texte sur du blanc :
                               posée sur le fond bleu nuit elle s'éteint. */
                            <Box sx={{
                              mb: 2, p: 1.5, borderRadius: 1,
                              bgcolor: 'rgba(52,211,153,.10)',
                              border: '1px solid rgba(52,211,153,.5)',
                            }}>
                              <Typography variant="caption" color="success.main" fontWeight="bold">
                                Instance HA enregistrée
                              </Typography>
                              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="caption" color="text.secondary">URL</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                      {r.ha_instance_url}
                                    </Typography>
                                    <Tooltip title="Copier">
                                      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(r.ha_instance_url); onSnack('Copié'); }}>
                                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="caption" color="text.secondary">Identifiant</Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {r.ha_username || 'admin'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="caption" color="text.secondary">Jeton longue durée</Typography>
                                  {/* Masqué par le serveur (8 derniers caractères) : il ouvre
                                      le direct de la box, il ne se lit ni ne se copie depuis
                                      le back-office. */}
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {r.ha_long_lived_token ? `…${String(r.ha_long_lived_token).slice(-8)}` : '—'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          )}

                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="caption" color="text.secondary">Identifiant du logement</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                  {r.id}
                                </Typography>
                                <Tooltip title="Copier">
                                  <IconButton size="small" onClick={() => { navigator.clipboard.writeText(r.id); onSnack('Copié'); }}>
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="caption" color="text.secondary">Identifiant du webhook</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                  {r.ha_webhook_id || '—'}
                                </Typography>
                                {r.ha_webhook_id && (
                                  <Tooltip title="Copier">
                                    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(r.ha_webhook_id); onSnack('Copié'); }}>
                                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="caption" color="text.secondary">Jeton du webhook</Typography>
                              {/* Masqué par le serveur, comme le jeton longue durée : il
                                  ne passe ni par l'écran ni par le presse-papiers. */}
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                {r.ha_api_token ? `…${String(r.ha_api_token).slice(-8)}` : '—'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Dialog open={roomDialog} onClose={() => setRoomDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau logement</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Nom *" value={roomForm.name} onChange={e => setRoomForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="N° chambre" value={roomForm.room_number} onChange={e => setRoomForm(f => ({ ...f, room_number: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Étage" value={roomForm.floor} onChange={e => setRoomForm(f => ({ ...f, floor: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Nom du résident" value={roomForm.resident_name} onChange={e => setRoomForm(f => ({ ...f, resident_name: e.target.value }))} /></Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Type de pièce</InputLabel>
                <Select value={roomForm.room_type} label="Type de pièce" onChange={e => setRoomForm(f => ({ ...f, room_type: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {ROOM_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={roomForm.description} onChange={e => setRoomForm(f => ({ ...f, description: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreateRoom} disabled={!roomForm.name || savingRoom}>
            {savingRoom ? <CircularProgress size={16} /> : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteRoomTarget} onClose={() => setDeleteRoomTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer le logement</DialogTitle>
        <DialogContent>
          <Typography>Supprimer <strong>{deleteRoomTarget?.name}</strong> ?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Cette action est irréversible.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteRoomTarget(null)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleDeleteRoom} disabled={deletingRoom}>
            {deletingRoom ? <CircularProgress size={18} color="inherit" /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
